import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Soup, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { unitLabel } from "@/lib/production-i18n";

const LEVEL = {
  duzo: { label: "Dużo", cls: "tt-sev-success" },
  malo: { label: "Mało", cls: "tt-sev-warning" },
  brak: { label: "Brak", cls: "tt-sev-critical" },
};

export default function ProductionOverview() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [items, setItems] = useState({});
  const [report, setReport] = useState(null);
  const [sugg, setSugg] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [c, p, r, s] = await Promise.all([
        api.get("/production/categories"),
        api.get("/production/products?is_active=true"),
        api.get("/production/report"),
        api.get("/production/suggestions").catch(() => ({ data: null })),
      ]);
      setCats(c.data); setProds(p.data); setItems(r.data.items || {}); setReport(r.data.report); setSugg(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd wczytywania"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const byCat = useMemo(() => {
    const m = {};
    prods.forEach((p) => { (m[p.category_id || "none"] = m[p.category_id || "none"] || []).push(p); });
    return m;
  }, [prods]);

  const counts = useMemo(() => {
    let brak = 0, malo = 0;
    Object.values(items).forEach((v) => { if (v.level === "brak") brak++; else if (v.level === "malo") malo++; });
    return { brak, malo };
  }, [items]);

  // P3 (2026-07-10): useMemo dla listy kategorii z produktami — filter/lookup na
  // każdym renderze byłby zbędnym kosztem gdy prods/byCat się nie zmieniają.
  const nonEmptyCats = useMemo(
    () => cats.filter((c) => (byCat[c.id] || []).length > 0),
    [cats, byCat]
  );

  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="production-overview-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tt-display flex items-center gap-2">
          <Soup className="h-6 w-6 text-[var(--tt-primary)]" />Produkcja — dziś
        </h1>
        <p className="text-[var(--tt-muted)] text-sm mt-1">
          {report?.status === "submitted"
            ? <>Raport wysłany {report.submitted_at ? <>o {new Date(report.submitted_at).toLocaleString("pl-PL", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</> : null}.</>
            : "Raport jeszcze niewysłany."}
          {" "}Na BRAK: <b>{counts.brak}</b> · na MAŁO: <b>{counts.malo}</b>.
        </p>
      </div>

      {/* Co przygotować dziś */}
      <Card className="tt-card">
        <CardContent className="p-4">
          <div className="font-semibold tt-display flex items-center gap-2 mb-3">
            <ClipboardList className="h-4 w-4 text-[var(--tt-primary)]" />Co przygotować dziś (sugestia)
            {sugg?.report_date && (
              <span className="ml-auto text-xs font-normal text-[var(--tt-muted)]">
                na podstawie raportu z {new Date(sugg.report_date).toLocaleDateString("pl-PL")}
              </span>
            )}
          </div>
          {(sugg?.to_prepare?.length || 0) === 0 && (sugg?.reminders?.length || 0) === 0 ? (
            <div className="text-sm text-[var(--tt-muted)]">
              {sugg?.based_on_report ? "Nic do dorobienia — stany OK." : "Brak raportu — sugestia pojawi się po wysłaniu stanu przez kuchnię."}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                {(sugg.to_prepare || []).map((x) => (
                  <div key={x.product_id} className="flex items-center justify-between text-sm">
                    <span>{x.name_pl}</span>
                    <span className="tt-mono font-semibold">{x.suggested_qty} {unitLabel(x.unit, "pl")}</span>
                  </div>
                ))}
              </div>
              {(sugg.reminders || []).length > 0 && (
                <div className="mt-3 pt-2 border-t border-[var(--tt-border)]">
                  <div className="text-xs text-[var(--tt-muted)] mb-1">Przypomnienia cykliczne (dziś):</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sugg.reminders.map((x) => <span key={x.product_id} className="tt-badge tt-sev-warning text-[11px]">{x.name_pl}</span>)}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dzisiejszy raport stanu */}
      <div className="space-y-3">
        <h2 className="tt-section-title">Dzisiejszy raport stanu</h2>
        {nonEmptyCats.map((c) => (
          <Card key={c.id} className="tt-card"><CardContent className="p-4">
            <div className="font-semibold tt-display mb-2">{c.name_pl}</div>
            <div className="divide-y divide-[var(--tt-border)]">
              {(byCat[c.id] || []).map((p) => {
                const it = items[p.id];
                const lv = it?.level ? LEVEL[it.level] : null;
                return (
                  <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium">{p.name_pl}</span>
                      <span className="text-xs text-[var(--tt-muted)] ml-2">{unitLabel(p.unit, "pl")}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {lv ? <span className={`tt-badge ${lv.cls} text-[11px]`}>{lv.label}</span>
                          : <span className="text-xs text-[var(--tt-muted)]">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
